import React, { useEffect, useState } from "react";
import SEOHead from "../components/SEOHead";
import blogPosts from "../data/blogPosts";
import { navClick, categorySlug, formatMonoDate } from "./Blog";
import "./blog.css";

function ContentBlock({ block, index }) {
  if (block.type === "h2") {
    return <h2 key={index}>{block.text}</h2>;
  }
  if (block.type === "list") {
    return (
      <ul key={index}>
        {block.items.map((item, itemIndex) => (
          <li key={itemIndex}>{item}</li>
        ))}
      </ul>
    );
  }
  return <p key={index}>{block.text}</p>;
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className={`blog-faq-item${isOpen ? " blog-faq-item-open" : ""}`}>
      <button type="button" className="blog-faq-trigger" onClick={onToggle}>
        <span>{item.q}</span>
        <span className="blog-faq-symbol" aria-hidden="true">+</span>
      </button>
      <div className="blog-faq-body">
        <div className="blog-faq-body-inner">
          <p className="blog-faq-answer">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

function NotFound({ onNavigate }) {
  return (
    <div className="blog-shell">
      <div className="blog-container">
        <header className="blog-topnav">
          <a href="/" className="blog-brand" onClick={navClick(onNavigate, "/")}>
            Spike Hunter Online <span>Trading Intelligence</span>
          </a>
          <a href="/blog" className="blog-back-link" onClick={navClick(onNavigate, "/blog")}>
            ← All posts
          </a>
        </header>

        <div className="blog-not-found">
          <div className="blog-not-found-code">404 / NO MATCHING POST</div>
          <h1>We couldn't find that article.</h1>
          <p>It may have been moved or the link is out of date.</p>
          <a
            href="/blog"
            className="blog-back-link"
            onClick={navClick(onNavigate, "/blog")}
          >
            ← Back to Trading Notes
          </a>
        </div>
      </div>

      <SEOHead
        title="Post Not Found | Spike Hunter Online"
        description="This blog post could not be found."
        path="/blog"
        noIndex
      />
    </div>
  );
}

export default function BlogPost({ slug, onNavigate }) {
  const post = blogPosts.find((candidate) => candidate.slug === slug);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!post) {
    return <NotFound onNavigate={onNavigate} />;
  }

  return (
    <div className="blog-shell">
      <div className="blog-container">
        <header className="blog-topnav">
          <a href="/" className="blog-brand" onClick={navClick(onNavigate, "/")}>
            Spike Hunter Online <span>Trading Intelligence</span>
          </a>
          <a href="/blog" className="blog-back-link" onClick={navClick(onNavigate, "/blog")}>
            ← All posts
          </a>
        </header>

        <nav className="blog-breadcrumb" aria-label="Breadcrumb">
          <a href="/" onClick={navClick(onNavigate, "/")}>
            Home
          </a>
          <span className="blog-breadcrumb-sep">/</span>
          <a href="/blog" onClick={navClick(onNavigate, "/blog")}>
            Blog
          </a>
          <span className="blog-breadcrumb-sep">/</span>
          <span className="blog-breadcrumb-current">{post.title}</span>
        </nav>

        <article className="blog-article">
          <div className="blog-article-meta">
            <span className={`blog-chip blog-chip-${categorySlug(post.category)}`}>
              {post.category}
            </span>
            <span className="blog-article-meta-sep" aria-hidden="true" />
            <time dateTime={post.publishDate}>{formatMonoDate(post.publishDate)}</time>
            <span className="blog-article-meta-sep" aria-hidden="true" />
            <span>{post.readTime} MIN READ</span>
          </div>

          <h1>{post.title}</h1>

          <div className="blog-article-body">
            {post.content.map((block, index) => (
              <ContentBlock block={block} index={index} key={index} />
            ))}
          </div>

          {post.faqs && post.faqs.length > 0 ? (
            <section className="blog-faq" aria-label="Frequently asked questions">
              <div className="blog-faq-heading">FAQ</div>
              {post.faqs.map((faq, index) => (
                <FaqItem
                  key={faq.q}
                  item={faq}
                  isOpen={openFaqIndex === index}
                  onToggle={() => setOpenFaqIndex(openFaqIndex === index ? -1 : index)}
                />
              ))}
            </section>
          ) : null}
        </article>
      </div>

      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        type="article"
        article={{
          title: post.metaTitle,
          description: post.metaDescription,
          publishDate: post.publishDate,
        }}
        faqs={post.faqs}
      />
    </div>
  );
}
